import React from 'react';
import ReportCard from '../components/ReportCard';
import DonutShare from '../components/DonutShare';
import InsightList from '../components/InsightList';
import { Figure, Mono } from '../components/Emphasis';
import { SEGMENT_FILL } from '../theme';
import {
  bucketCount, countAtLeast, fmtCount, fmtPct, rankByPrevalence, share, shareAtLeast, spreadAtTwoPlus, tip,
} from '../format';

const LEGEND_LABELS = ['Zero', 'One', 'Two', 'Three or more'];

/**
 * Chapter 02 opener: the whole distribution as one donut, with the two-or-more figure called out
 * beneath the legend because that is the group the chapter is arguing about.
 */
const DeficiencyAtAGlance = ({ data }) => {
  const total = data.cohort.total;
  const twoPlusShare = shareAtLeast(data.buckets, 2);
  const twoPlusCount = countAtLeast(data.buckets, 2);
  const topCount = bucketCount(data.buckets, 3);
  const ranked = rankByPrevalence(data.markers);
  const leader = ranked[0];
  const topPair = (data.co_occurrence || [])[0];
  const genderSharper = spreadAtTwoPlus(data.by_gender) > spreadAtTwoPlus(data.by_age);
  const pairLabels = topPair
    ? topPair.pair.map((key) => (data.markers.find((marker) => marker.key === key) || {}).label || key)
    : [];

  const segments = data.buckets.map((bucket, index) => ({
    label: LEGEND_LABELS[index],
    length: share(bucket.count, total),
    key: bucket.flags,
  }));

  return (
    <div className='mt-[36px] grid grid-cols-1 items-start gap-ds-5 lg:grid-cols-[1fr_1.15fr]'>
      <ReportCard fileName='deficiency-at-a-glance' className='p-[26px]'>
        <h3
          className='m-0 mb-1 font-fig-extrabold text-[26px] font-bold leading-[1.18] text-gray-900'
          style={{ textWrap: 'pretty' }}
        >
          {`Nearly ${(twoPlusShare / 10).toFixed(0)} in 10 employees carry two or more overlapping deficiencies`}
        </h3>
        <p className='m-0 mb-ds-6 text-xs text-gray-600'>
          {`Abnormality burden across ${data.cohort.markers} tests`}
        </p>

        <div className='flex flex-wrap items-center gap-[30px]'>
          <DonutShare
            segments={segments}
            centerValue={fmtCount(total)}
            centerCaption='employees screened'
          />
          <div className='grid min-w-[180px] flex-1 gap-ds-3'>
            {data.buckets.map((bucket, index) => (
              <div
                key={bucket.flags}
                data-tip={tip(
                  `${LEGEND_LABELS[index]} abnormalit${index === 1 ? 'y' : 'ies'}`,
                  `${fmtCount(bucket.count)} employees`
                )}
                className='flex items-center gap-ds-3 text-xs text-gray-800'
              >
                <span className={`h-2.5 w-2.5 rounded-[3px] ${SEGMENT_FILL[index]}`} />
                {LEGEND_LABELS[index]}
                <span
                  className={`ml-auto font-jetbrains-mono font-semibold ${
                    index === 3 ? 'text-ds-rose-dk' : 'text-gray-900'
                  }`}
                >
                  {fmtPct(share(bucket.count, total))}
                </span>
              </div>
            ))}
            <div className='border-t border-gray-300 pt-ds-3 text-xs font-semibold text-gray-900'>
              Two or more
              <span className='ml-ds-2 font-jetbrains-mono text-gray-900'>{fmtPct(twoPlusShare)}</span>
              <span className='mt-[3px] block text-ts-xs font-normal text-gray-600'>
                {`${fmtCount(twoPlusCount)} employees`}
              </span>
            </div>
          </div>
        </div>
      </ReportCard>

      <div className='rounded-ds-lg border border-gray-300 bg-white p-[26px]'>
        <div className='mb-ds-4 text-ts-xs font-semibold uppercase tracking-[0.12em] text-ds-brand'>
          Key takeaways
        </div>
        <InsightList
          divided
          items={[
            leader ? (
              <>
                {leader.label} is the single largest issue — <Mono>{fmtPct(leader.prevalence)}</Mono> of those
                tested flagged abnormal.
              </>
            ) : null,
            genderSharper ? (
              <>Gender — not age — is the sharpest fault line in this data.</>
            ) : (
              <>Age bands separate this cohort more sharply than gender does.</>
            ),
            topPair && topPair.count ? (
              <>
                {pairLabels.join(' + ')} co-occur in{' '}
                <Figure>{fmtPct(share(topPair.count, total))}</Figure> of the workforce, pointing to a shared
                lifestyle cause.
              </>
            ) : null,
            <>
              <Mono>{`${fmtCount(topCount)} employees (${fmtPct(share(topCount, total))})`}</Mono> carry three or
              more abnormalities — a priority group for direct follow-up.
            </>,
          ]}
        />
      </div>
    </div>
  );
};

export default DeficiencyAtAGlance;
